import { allEnumValues, EpsonBarcodeFormat, EpsonBarcodeFormatHelper, ExpoPrinterMode, GuestReceiptTextSize, KioskGuestReceiptMode, UserRight, } from '@biteinc/enums';
export const receiptSettingsSchema = {
    // Location Model Settings
    // Receipt SETTINGS
    kioskGuestReceiptMode: {
        displayName: 'Kiosk Guest Receipt Mode',
        type: 'stringEnum',
        enum: KioskGuestReceiptMode,
        required: true,
        ui: 'dropdown',
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    expoPrinterMode: {
        displayName: 'Expo Printer Mode',
        type: 'stringEnum',
        enum: ExpoPrinterMode,
        required: true,
        ui: 'dropdown',
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    hideCouponCodes: {
        type: 'bool',
        required: true,
        displayName: 'Hide Coupon Names and Codes',
        defaultValue: false,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'Show the COUPON custom string (default: "Coupon") instead of coupon names and codes on new printed receipts and kiosk order summaries. Digital receipts also hide names and codes, but show "Coupon". Applies to POS, loyalty, and Bite coupons. Discount amounts stay visible. Existing orders are not changed.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    printDefaultMods: {
        displayName: 'Print Default Mods',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'Affects all methods of printing.',
            isWarning: true,
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    printZeroDollarMods: {
        displayName: 'Print Zero Dollar Mods',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'Affects all methods of printing.',
            isWarning: true,
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    printDeselectedMods: {
        displayName: 'Print Deselected Mods',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'Will prefix deselected mods with DESELECTED_MOD_PREFIX custom string. Affects all methods of printing.',
            isWarning: true,
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    printTransactionReceiptText: {
        displayName: 'Print Transaction Receipt Text',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'Print the transaction information provided by the payment integration, the section on the bottom half of the receipt.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    printModCodes: {
        displayName: 'Print Mod Codes',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'The name of any sub mod codes that are automatically added will be prefixed before the name of the parent item.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    printQRCodeWithPosOrderID: {
        displayName: 'Print QR Code with POS Order ID',
        type: 'keyString',
        ui: 'dropdown',
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'The selected POS order ID will be printed at the bottom of the customer, kitchen, and expo receipts.',
        },
        isNullable: true,
        writeRight: UserRight.ManageChannelSettings,
    },
    includeTaxTotalOnReceipts: {
        type: 'bool',
        displayName: 'Print Tax Total',
        required: true,
        tabId: 'receipt-settings-v2',
        defaultValue: true,
        tooltip: {
            text: 'It may be prudent to hide the tax total where we do not have the calculated tax.',
        },
    },
    vendorInstructionsSize: {
        displayName: 'Vendor Instructions Size',
        type: 'stringEnum',
        enum: GuestReceiptTextSize,
        required: true,
        ui: 'dropdown',
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'The size the vendor name will appear on the guest receipt.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    guestReceiptBodyTextSize: {
        displayName: 'Guest Receipt Body Text Size',
        type: 'stringEnum',
        enum: GuestReceiptTextSize,
        values: [GuestReceiptTextSize.Small, GuestReceiptTextSize.Medium],
        required: true,
        ui: 'dropdown',
        tabId: 'receipt-settings-v2',
        defaultValue: GuestReceiptTextSize.Small,
        tooltip: {
            text: 'The size of the body text (items, mods, prices, totals) on the printed guest receipt. Small is the compact default; Medium prints the body in the printer’s larger standard font. Built-in kiosk printers always print Small.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    showReprintButton: {
        displayName: 'Reprint receipt button on success or failures (Kiosk Only)',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'Adds a Print Receipt button to error and checkout complete screen.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    alertGuestWhenPrinterIsOutOfPaper: {
        displayName: 'Alert guest when the printer is out of paper (Kiosk Only)',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'Keeps the Print Receipt button enabled when the printer is out of paper and shows the guest a message instead of attempting to print.',
        },
        writeRight: UserRight.ManageChannelSettings,
        exclusiveTo: 'vitrine',
    },
    receiptHeaderText: {
        displayName: 'Printed Receipt Header (Kiosk Only)',
        type: 'codeString',
        isNullable: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: `
          Valid Tags:
          Font Size:
          <s1>Size 1 Text</s1>
          <s2>Size 2 Text</s2>
          <s3>Size 3 Text</s3>

          Font Weight:
          <b>Bold Text</b>

          QR Code:
          <qrcode>https://admin.getbite.com/</qrcode>

          QR Code Size (Default to 6 if not provided, Min: 3, Max: 10):
          <qrcode size="3">https://admin.getbite.com/</qrcode>
          <qrcode size="10">https://admin.getbite.com/</qrcode>
        `,
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    receiptHeaderImage: {
        displayName: 'Receipt Header Image',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        isHidden: true,
        keepAspectRatio: 'contain',
        size: {
            width: 240,
            height: 240,
        },
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'If provided, this image is used in place of the logo at the top of the receipt.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    receiptFooterText: {
        displayName: 'Printed Receipt Footer (Kiosk Only)',
        type: 'codeString',
        isNullable: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: `
          Valid Tags:
          Font Size:
          <s1>Size 1 Text</s1>
          <s2>Size 2 Text</s2>
          <s3>Size 3 Text</s3>

          Font Weight:
          <b>Bold Text</b>

          QR Code:
          <qrcode>https://admin.getbite.com/</qrcode>

          QR Code Size (Default to 6 if not provided, Min: 3, Max: 10):
          <qrcode size="3">https://admin.getbite.com/</qrcode>
          <qrcode size="10">https://admin.getbite.com/</qrcode>
        `,
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    receiptFooterImage: {
        displayName: 'Receipt Footer Image',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        isHidden: true,
        keepAspectRatio: 'contain',
        size: {
            width: 240,
            height: 240,
        },
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'If provided, this image is used in place of the logo at the bottom of the receipt.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    printOrderNumberBarcode: {
        displayName: 'Add an order number barcode to the receipt',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        biteOnly: true,
        tooltip: {
            text: 'Allows the guest to take the receipt and pay for the order at a common-pay station (e.g. cafeteria checkout).\nNOTE: Currently only works with Infor, Simphony, & NCR Radiant locations.',
        },
    },
    orderNumberBarcodeFormat: {
        displayName: 'Barcode format for order number',
        type: 'int',
        values: allEnumValues(EpsonBarcodeFormat),
        required: true,
        enumNameHelper: EpsonBarcodeFormatHelper.name,
        enum: EpsonBarcodeFormat,
        tabId: 'receipt-settings-v2',
        biteOnly: true,
        ui: 'dropdown',
        conditions: [
            {
                field: 'printOrderNumberBarcode',
                values: [true],
            },
        ],
    },
    printOrderedItemPluWithOrderedItems: {
        displayName: 'Print Ordered Item PLUs Inline with Name',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'Print the PLU for all ordered items next to the name and the price.',
        },
    },
    orderedItemPlusQrCodeConfig: {
        displayName: 'Config for QR Code with Ordered Item PLUs',
        type: 'object',
        required: true,
        isNullable: true,
        allowEmpty: true,
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'Print a QR code with the PLUs of all ordered items at the bottom of the customer, kitchen, and expo receipts.',
        },
        fields: {
            header: {
                displayName: 'QR Code Header',
                type: 'keyString',
            },
            itemDelimiter: {
                displayName: 'Item Delimiter',
                type: 'keyString',
            },
            prefixFor6DigitPlus: {
                displayName: 'Prefix for 6-Digit PLUs',
                type: 'keyString',
                placeholder: 'Prefix for 6-digit PLUs',
                tooltip: {
                    text: 'Prefix to use for each 6-digit item PLU in the QR code',
                },
            },
            prefixFor12DigitPlus: {
                displayName: 'Prefix for 12-Digit PLUs',
                type: 'keyString',
                placeholder: 'Prefix for 12-digit PLUs',
                tooltip: {
                    text: 'Prefix to use for each 12-digit item PLU in the QR code',
                },
            },
            prefixFor39DigitPlus: {
                displayName: 'Prefix for 39-Digit PLUs',
                type: 'keyString',
                placeholder: 'Prefix for 39-digit PLUs',
                tooltip: {
                    text: 'Prefix to use for each 39-digit item PLU in the QR code',
                },
            },
            prefixForOtherPlus: {
                displayName: 'Prefix for Other PLUs',
                type: 'keyString',
                placeholder: 'Prefix for other PLUs',
                tooltip: {
                    text: 'Prefix to use for each item PLU in the QR code that is not 6, 12 or 39 digits long or the prefix for PLUs of that length is not provided',
                },
            },
            includeFreeMods: {
                displayName: 'Include $0 Mods?',
                type: 'bool',
            },
        },
    },
    emailReceiptsEnabled: {
        displayName: 'Allow email receipts',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    textReceiptsEnabled: {
        displayName: 'Allow text receipts',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'Requires an SMS integration to be set up for each channel',
            isWarning: true,
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    qrCodeReceiptsEnabled: {
        displayName: 'Allow QR code receipts',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    emailSaleReceiptSubjectTemplate: {
        displayName: 'Email Receipt Subject Template (Sale)',
        type: 'keyString',
        isNullable: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'If not provided, it will default to "Receipt from Brand Name".',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    emailRefundReceiptSubjectTemplate: {
        displayName: 'Email Receipt Subject Template (Refund)',
        type: 'keyString',
        isNullable: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'If not provided, it will default to "Refund Receipt from Brand Name".',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    emailReceiptHeaderImg: {
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        displayName: 'Email Receipt Header Image',
        keepAspectRatio: 'contain',
        size: {
            width: 240,
            height: 240,
        },
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'If provided, this image is used in place of the logo at the top of the email receipt.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    emailReceiptHeaderText: {
        displayName: 'Email Receipt Header',
        type: 'codeString',
        isNullable: true,
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    emailReceiptFooterText: {
        displayName: 'Email Receipt Footer',
        type: 'codeString',
        isNullable: true,
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    digitalReceiptHeaderText: {
        displayName: 'Digital Receipt Header (receipt link SMS)',
        type: 'codeString',
        isNullable: true,
        tabId: 'receipt-settings-v2',
        tooltip: {
            text: 'Header text for virtual receipt.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    digitalReceiptFooterText: {
        displayName: 'Digital Receipt Footer (receipt link SMS)',
        type: 'codeString',
        isNullable: true,
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    emailKioskReceiptIfAddressIsProvided: {
        displayName: 'Automatically Email Kiosk Receipt If Address Is Provided',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    includeStoredValueRemainingBalanceOnReceipts: {
        displayName: 'Include Stored Value Remaining Balance On Receipts',
        type: 'bool',
        required: true,
        tabId: 'receipt-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'Olo Only. Please ask Bite if needed for other integrations.',
        },
    },
};
//# sourceMappingURL=settings_receipt_schema.js.map